import React, { useState, useEffect } from 'react'

import { useHistory, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import Container from 'presentation/hospital/components/Container'
import { LoadPatientInfo } from 'domain/usecases/patient/load-patient-info'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import { Hospital } from 'domain/entities/hospital-model'
import { useServices } from 'presentation/hooks/use-services'
import { SurgicalOrderModel } from 'domain/entities/surgical-order-model'
import { LoadSurgicalOrderDocument } from 'domain/usecases/surgical-order/load-surgical-order-document'
import { useStores } from 'presentation/hooks/use-stores'

import * as S from './styles'
import { SolicitationCancelOrderForm } from 'presentation/hospital/components/Forms/SolicitationCancelOrderForm'
import { ReasonsCancellationModel } from 'domain/entities/reasons-cancellation'

type Props = {
  loadPatientInfo: LoadPatientInfo
  loadSurgicalOrderDocument?: LoadSurgicalOrderDocument
} & WithLoadingProps

export const SolicitationCancelOrderLayout = WithLoading(
  ({ loadPatientInfo, loadSurgicalOrderDocument, setIsLoading }: Props) => {
    const [patientInfo, setPatientInfo] = useState({} as LoadPatientInfo.Model)
    const [hospitals, setHospitals] = useState<Hospital[]>([])
    const [reasons, setReasons] = useState<ReasonsCancellationModel[]>()
    const [surgicalOrder, setSurgicalOrder] = useState<SurgicalOrderModel>()
    const location = useLocation<any>()
    const history = useHistory()

    const currentHospital = useStores().currentHospital

    const service = useServices().surgicalOrder

    async function loadData() {
      try {
        setIsLoading(true)
        await loadPatient()
        await loadHospitalsData()
        await loadSurgicalOrder()
      } catch (error: any) {
        toast.error(error.message)
      } finally {
        setIsLoading(false)
      }
    }

    async function loadPatient() {
      const patient = await loadPatientInfo.load(
        ['user_id', 'name', 'birthday', 'phone', 'cpf', 'email', 'gender'],
        location.state?.order
          ? location.state?.order?.patient?.patient_id
          : location.state?.patient_id
      )
      setPatientInfo(patient)
    }

    async function loadHospitalsData() {
      await currentHospital.load()
      setHospitals(currentHospital.hospitalList ?? [])
    }

    async function loadSurgicalOrder() {
      const surgicalOrder = await service.loadSurgicalOrder(
        Number(
          location.state?.order
            ? location.state?.order?.surgical_order_id
            : location.state?.surgical_order_id
        ),
        [
          'surgical_order_id',
          'updatedAt',
          'justificationToDelete',
          'hospital_id',
          'patient{gender',
          'name',
          'cpf',
          'phone',
          'landlinePhone',
          'email',
          'birthday',
          'patient_id',
          'company',
          'maritalStatus}',
          'documents {document_id, type, name}',
          'pendencies {surgical_pendency_id, surgical_order_id, observation, answer, answeredAt, impediment, status, createdAt}',
          'cancelledInfo {cancelledCollaboratorRegister, justification, cancelledAt, cancelledBy {name, cpf}}',
          'pendencies {surgical_pendency_id, surgical_order_id, observation, answer, answeredAt, impediment, status, createdAt}',
          'cancellation_request {reason, created_at, surgical_cancellation_id, comments {comment, created_at, updated_at}, requested_by {name}}',
          'healthInsurance {healthCard, healthInsuranceName, healthPlanName, company, validThru}'
        ]
      )
      if (!surgicalOrder) {
        toast.error('Pedido cirúrgico não encontrado.')
        history.push('/home')
        return
      }

      setSurgicalOrder(surgicalOrder)
      if (surgicalOrder.cancelledInfo) {
        history.push('/home')
      }
    }

    async function loadReasonsCancellation() {
      const respose: any = await service.loadReasonsCancellation()
      setReasons(respose.data)
    }

    useEffect(() => {
      loadData()
      loadReasonsCancellation()
    }, [])

    return (
      <Container>
        <S.TableTitle>Cancelar pedido cirúrgico</S.TableTitle>
        <S.Description>
          Confira as informações do pedido e clique em cancelar ao final da
          página
        </S.Description>
        <S.Wrapper>
          <SolicitationCancelOrderForm
            reasons={reasons}
            requester={surgicalOrder?.cancellation_request?.requested_by?.name}
            onlyVisualization
            surgicalOrder={surgicalOrder}
            loadPatientInfo={patientInfo}
            hospitals={hospitals}
            loadSurgicalOrderDocument={loadSurgicalOrderDocument}
          />
        </S.Wrapper>
      </Container>
    )
  }
)
